import { createContext, useContext, useEffect, useState } from "react";

/**
 * How wide a list page's heading is drawn, which `ListPageHeading` measures and its filters read
 * (#6720). The room the filters get is not the window's: the title row's search, Views menu and
 * create control take a share of it that differs per page. Undefined until it is measured, and where
 * the browser cannot measure it.
 */
export const ListHeadingWidth = createContext<number | undefined>(undefined);

/** Below this width a heading's filters are drawn compact: the tabs and full-size filters do not share a row. */
export const COMPACT_FILTERS_BELOW = 760;

/** Whether the heading the filters sit in is narrower than `COMPACT_FILTERS_BELOW`. */
export function useHeadingIsNarrow(): boolean {
  const width = useContext(ListHeadingWidth);
  return width !== undefined && width < COMPACT_FILTERS_BELOW;
}

/** The width of the element handed to the returned ref, kept current as it resizes. */
export function useMeasuredWidth(): [(element: HTMLElement | null) => void, number | undefined] {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return [setElement, width];
}
